<script setup>
// ─── Kanban de Atendimento (Interart) ───────────────────────────────────────
// Visão alternativa à lista: colunas drag-and-drop estilo Kommo/Zoho.
// • Colunas = valores do custom attribute (lista) "kanban_stage" (infra nativa).
// • Etapa da conversa = custom_attributes.kanban_stage ⇒ broadcast nativo = tempo real.
// • Dinâmica de resposta: usa o campo NATIVO waiting_since (cliente aguardando
//   resposta desde X; zera quando o atendente responde) — card muda de estado
//   sozinho, sem mover de coluna (etapa é manual; estado é automático).
// • Sem dependência nova: vuedraggable já era do bundle.
import { ref, computed, watch, onMounted, onBeforeUnmount } from 'vue';
import { useRouter, useRoute } from 'vue-router';
import axios from 'axios';
import draggable from 'vuedraggable';
import { useStore } from 'dashboard/composables/store';
import { useAlert } from 'dashboard/composables';
import { useUISettings } from 'dashboard/composables/useUISettings';
import ConversationApi from 'dashboard/api/inbox/conversation';
import SearchAPI from 'dashboard/api/search';
import ContactAPI from 'dashboard/api/contacts';
import NextButton from 'dashboard/components-next/button/Button.vue';
import ConversationBox from 'dashboard/components/widgets/conversation/ConversationBox.vue';

const KANBAN_KEY = 'kanban_stage';
const ENTRADA = '__entrada__';
const DEFAULT_STAGES = [
  '💬 Em atendimento',
  '⏳ Aguardando cliente',
  '🎨 Arte / Produção',
  '📦 Pronto p/ retirada',
];

const store = useStore();
const router = useRouter();
const route = useRoute();

const loading = ref(true);
const savingCols = ref(false);
const dragging = ref(false);
const convs = ref([]); // master: conversas abertas
const columns = ref({}); // stage -> [convs]
const menuOpenFor = ref(null); // coluna com menu ⋮ aberto

// Filtros / modo de visão — PERSISTEM por atendente (localStorage): F5/reabrir
// mantém a visão que a pessoa deixou ativa.
const PREFS_KEY = 'interart_kanban_prefs';
const viewMode = ref('fluxo'); // 'fluxo' = tudo · 'foco' = só a responder
const filterLabel = ref(null); // etiqueta selecionada (dono do lead)
const filterMine = ref(false); // só atribuídas a mim
try {
  const prefs = JSON.parse(localStorage.getItem(PREFS_KEY) || '{}');
  // viewMode NÃO é mais restaurado — o toggle Fluxo/A responder saiu da UI
  // (2026-07-03); todo mundo abre no fluxo completo (pendentes já sobem ao topo).
  if (typeof prefs.filterLabel === 'string' && prefs.filterLabel)
    filterLabel.value = prefs.filterLabel;
} catch (e) {
  /* prefs corrompidas → ignora */
}
watch([viewMode, filterLabel], () => {
  try {
    localStorage.setItem(
      PREFS_KEY,
      JSON.stringify({ viewMode: viewMode.value, filterLabel: filterLabel.value })
    );
  } catch (e) {
    /* storage cheio/indisponível → segue sem persistir */
  }
});

// Pesquisa ÚNICA: digitou (2+ letras) → busca TUDO na hora (busca nativa:
// conversas + mensagens + contatos, abertas E resolvidas), mais recentes no topo.
const searchQ = ref('');
const searchResults = ref([]);
const searching = ref(false);
let searchTimer = null;

const showSearchResults = computed(() => searchQ.value.trim().length >= 2);

watch(searchQ, () => {
  clearTimeout(searchTimer);
  const q = searchQ.value.trim();
  if (q.length < 2) {
    searchResults.value = [];
    return;
  }
  searching.value = true;
  searchTimer = setTimeout(async () => {
    try {
      // Mesmas 3 fontes da lupa nativa. ATENÇÃO ao formato: payload é um
      // OBJETO ({conversations|messages|contacts}) — não a lista direto.
      const [convRes, msgRes, contRes] = await Promise.all([
        SearchAPI.conversations({ q })
          .then(r => r?.data?.payload?.conversations || [])
          .catch(() => []),
        SearchAPI.messages({ q })
          .then(r => r?.data?.payload?.messages || [])
          .catch(() => []),
        SearchAPI.contacts({ q })
          .then(r => r?.data?.payload?.contacts || [])
          .catch(() => []),
      ]);
      const seen = new Set();
      const items = [];
      convRes.forEach(cv => {
        const id = cv?.id;
        if (!id || seen.has(id)) return;
        seen.add(id);
        items.push({
          id,
          ts: Number(cv?.timestamp || cv?.last_activity_at || 0),
          title:
            cv?.contact?.name || cv?.meta?.sender?.name || `Conversa #${id}`,
          phone:
            cv?.contact?.phone_number || cv?.meta?.sender?.phone_number || '',
          status: cv?.status || '',
          snippet: '',
        });
      });
      msgRes.forEach(m => {
        const id = m?.conversation_id;
        if (!id) return;
        const snippet = (m?.content || '').replace(/\s+/g, ' ').slice(0, 90);
        const ts = Number(m?.created_at || 0);
        if (seen.has(id)) {
          const it = items.find(x => x.id === id);
          if (it) {
            if (!it.snippet) it.snippet = snippet;
            if (ts > it.ts) it.ts = ts;
          }
          return;
        }
        seen.add(id);
        items.push({
          id,
          ts,
          title: m?.contact?.name || `Conversa #${id}`,
          phone: m?.contact?.phone_number || '',
          status: m?.conversation?.status || '',
          snippet,
        });
      });
      // Contatos (nome/telefone/email) — clicar resolve a conversa mais recente
      const contactSeen = new Set();
      contRes.forEach(ct => {
        if (!ct?.id || contactSeen.has(ct.id)) return;
        contactSeen.add(ct.id);
        items.push({
          id: `contact-${ct.id}`,
          contactId: ct.id,
          isContact: true,
          ts: Number(ct?.last_activity_at || 0),
          title: ct?.name || `Contato #${ct.id}`,
          phone: ct?.phone_number || ct?.email || '',
          status: 'contato',
          snippet: 'Contato — clique pra abrir a conversa mais recente',
        });
      });
      // Estética: mais recentes no topo
      items.sort((a, b) => (b.ts || 0) - (a.ts || 0));
      searchResults.value = items.slice(0, 60);
    } finally {
      searching.value = false;
    }
  }, 450);
});

// Contato achado na busca → resolve a conversa mais recente dele e abre no painel
async function openContactResult(item) {
  try {
    const resp = await ContactAPI.getConversations(item.contactId);
    const list = resp?.data?.payload || [];
    if (!list.length) {
      useAlert('Esse contato ainda não tem conversas');
      return;
    }
    list.sort((a, b) => (b.last_activity_at || 0) - (a.last_activity_at || 0));
    openPanel({ id: list[0].id });
  } catch (e) {
    useAlert('Erro ao abrir conversa do contato');
  }
}

function openSearchResult(r) {
  if (r.isContact) openContactResult(r);
  else openPanel({ id: r.id });
}

// Etiquetas de VENDEDOR (fonte de verdade: config da Etiqueta Automática).
// Filtrar por um vendedor mostra as dele + as SEM dono ("disponíveis"): quem
// responder primeiro leva (autotag aplica a etiqueta) e o card some dos outros.
const vendedorLabels = ref([]);

function semVendedor(c) {
  if (!vendedorLabels.value.length) return false;
  return !(c.labels || []).some(l => vendedorLabels.value.includes(l));
}

// ── Definição das colunas (custom attribute lista) ──────────────────────────
const definition = computed(() =>
  (store.getters['attributes/getConversationAttributes'] || []).find(
    d => d.attributeKey === KANBAN_KEY
  )
);
const stages = computed(() => definition.value?.attributeValues || []);
const boardReady = computed(() => !!definition.value);

const currentUserId = computed(() => store.getters.getCurrentUserID);

// Mapa título → cor real das etiquetas do Chatwoot
const labelColorMap = computed(() => {
  const map = {};
  (store.getters['labels/getLabels'] || []).forEach(l => {
    map[l.title] = l.color;
  });
  return map;
});

// ── Estado de resposta (nativo do Chatwoot) ─────────────────────────────────
function needsReply(c) {
  return (c.waiting_since || 0) > 0;
}

function waitingMinutes(c) {
  if (!needsReply(c)) return 0;
  return Math.max(0, Math.floor((Date.now() / 1000 - c.waiting_since) / 60));
}

function fmtMin(m) {
  if (m < 60) return `${m}min`;
  if (m < 1440) return `${Math.floor(m / 60)}h`;
  return `${Math.floor(m / 1440)}d`;
}

// ── Filtros aplicados ao quadro ──────────────────────────────────────────────
// Filtro de "dono" (vendedor/etiqueta/Meus) — compartilhado entre o quadro e
// os CONTADORES do topo, pra tudo acompanhar o filtro clicado.
function passaFiltroDono(c) {
  if (filterLabel.value) {
    const tem = (c.labels || []).includes(filterLabel.value);
    const ehVendedor = vendedorLabels.value.includes(filterLabel.value);
    // Filtro de VENDEDOR inclui também as sem dono (pool); etiqueta comum filtra exato.
    if (ehVendedor) {
      if (!tem && !semVendedor(c)) return false;
    } else if (!tem) return false;
  }
  if (
    filterMine.value &&
    (c.meta?.assignee?.id || null) !== currentUserId.value
  )
    return false;
  return true;
}

const filteredConvs = computed(() =>
  convs.value.filter(c => {
    if (c.status !== 'open') return false;
    if (viewMode.value === 'foco' && !needsReply(c)) return false;
    return passaFiltroDono(c);
  })
);


// Contadores do topo acompanham o filtro ativo (vendedor/etiqueta), não o global.
const totalCount = computed(
  () =>
    convs.value.filter(c => c.status === 'open' && passaFiltroDono(c)).length
);
const pendingTotal = computed(
  () =>
    convs.value.filter(
      c => c.status === 'open' && needsReply(c) && passaFiltroDono(c)
    ).length
);

// Etiquetas presentes no quadro (pra barra de filtro), mais usadas primeiro
const boardLabels = computed(() => {
  const counts = {};
  convs.value
    .filter(c => c.status === 'open')
    .forEach(c =>
      (c.labels || []).forEach(l => {
        counts[l] = (counts[l] || 0) + 1;
      })
    );
  // Vendedores SEMPRE visíveis (mesmo com 0 cards) — são as "visões" de cada
  // atendente; sem isso o chip some quando o vendedor zera o quadro.
  vendedorLabels.value.forEach(l => {
    if (!(l in counts)) counts[l] = 0;
  });
  return Object.entries(counts)
    .sort((a, b) => {
      // etiquetas de vendedor primeiro, depois por volume
      const va = vendedorLabels.value.includes(a[0]) ? 0 : 1;
      const vb = vendedorLabels.value.includes(b[0]) ? 0 : 1;
      if (va !== vb) return va - vb;
      return b[1] - a[1];
    })
    .slice(0, 8)
    .map(([title, count]) => ({
      title,
      count,
      color: labelColorMap.value[title] || '#64748b',
    }));
});

// ── Montagem do quadro ───────────────────────────────────────────────────────
function stageOf(c) {
  const s = c.custom_attributes && c.custom_attributes[KANBAN_KEY];
  return s && stages.value.includes(s) ? s : ENTRADA;
}

function rebuild() {
  const map = {};
  map[ENTRADA] = [];
  stages.value.forEach(s => {
    map[s] = [];
  });
  filteredConvs.value.forEach(c => map[stageOf(c)].push(c));
  // Quem espera resposta há MAIS tempo fica no topo; depois, mais recentes.
  Object.keys(map).forEach(k =>
    map[k].sort((a, b) => {
      const na = needsReply(a);
      const nb = needsReply(b);
      if (na !== nb) return na ? -1 : 1;
      if (na && nb) return (a.waiting_since || 0) - (b.waiting_since || 0);
      return (b.timestamp || 0) - (a.timestamp || 0);
    })
  );
  columns.value = map;
}

function setMode(m) {
  viewMode.value = m;
  rebuild();
}
function setLabelFilter(l) {
  filterLabel.value = filterLabel.value === l ? null : l;
  rebuild();
}
function toggleMine() {
  filterMine.value = !filterMine.value;
  rebuild();
}

function pendCount(s) {
  return (columns.value[s] || []).filter(needsReply).length;
}

// ── Carregamento PROGRESSIVO (1ª página instantânea, resto em paralelo) ─────
function fetchPage(page) {
  return ConversationApi.get({
    status: 'open',
    assigneeType: 'all',
    page,
    sortBy: 'latest',
  })
    .then(r => r?.data?.data || r?.data || {})
    .catch(() => ({}));
}

function addConvs(payload, seen) {
  (payload || []).forEach(c => {
    if (!seen.has(c.id)) {
      seen.add(c.id);
      convs.value.push(c);
    }
  });
}

async function fetchAll() {
  loading.value = true;
  try {
    const seen = new Set();
    convs.value = [];
    const first = await fetchPage(1);
    addConvs(first.payload, seen);
    rebuild();
    const allCount = (first.meta && first.meta.all_count) || 0;
    const totalPages = Math.min(Math.ceil(allCount / 25) || 1, 24);
    for (let start = 2; start <= totalPages; start += 4) {
      const batch = [];
      for (let p = start; p < start + 4 && p <= totalPages; p += 1) {
        batch.push(fetchPage(p));
      }
      // eslint-disable-next-line no-await-in-loop
      const results = await Promise.all(batch);
      results.forEach(b => addConvs(b.payload, seen));
      if (!dragging.value) rebuild();
    }
  } catch (e) {
    useAlert('Falha ao carregar conversas do quadro');
  } finally {
    loading.value = false;
  }
}

// ── Tempo real: espelha mutações da store (ActionCable) no quadro ────────────
let unsubscribe = null;
let syncTimer = null;

function mergeFromStore() {
  const all = store.getters.getAllConversations || [];
  const byId = new Map(convs.value.map(c => [c.id, c]));
  all.forEach(sc => {
    const mine = byId.get(sc.id);
    if (mine) {
      mine.status = sc.status;
      mine.custom_attributes = sc.custom_attributes;
      mine.timestamp = sc.timestamp;
      mine.unread_count = sc.unread_count;
      mine.waiting_since = sc.waiting_since;
      mine.meta = sc.meta || mine.meta;
      mine.labels = sc.labels || mine.labels;
      mine.messages = sc.messages || mine.messages;
      mine.last_non_activity_message =
        sc.last_non_activity_message || mine.last_non_activity_message;
    } else if (sc.status === 'open') {
      convs.value.push(sc);
    }
  });
  rebuild();
}

function scheduleSync() {
  if (dragging.value) return;
  clearTimeout(syncTimer);
  syncTimer = setTimeout(mergeFromStore, 700);
}

onMounted(async () => {
  store.dispatch('attributes/get');
  store.dispatch('labels/get');
  // Etiquetas de vendedor = mappings da Etiqueta Automática (fonte de verdade).
  // Auth manual: o axios default NÃO carrega os headers do agente neste build —
  // monta a partir do cookie cw_d_session_info (padrão comprovado do autotag/Index.vue).
  const authHeaders = (() => {
    try {
      const m = document.cookie.match(/cw_d_session_info=([^;]+)/);
      if (!m) return {};
      const parsed = JSON.parse(decodeURIComponent(m[1]));
      const h = {};
      ['access-token', 'client', 'uid', 'expiry', 'token-type'].forEach(k => {
        if (parsed[k]) h[k] = parsed[k];
      });
      return h;
    } catch (e) {
      return {};
    }
  })();
  axios
    .get('/api/v2/interart/autotag_config', {
      headers: authHeaders,
      withCredentials: true,
    })
    .then(r => {
      vendedorLabels.value = (r.data?.mappings || [])
        .map(m => m.label)
        .filter(Boolean);
      rebuild();
    })
    .catch(() => {});
  await fetchAll();
  unsubscribe = store.subscribe(mutation => {
    if ((mutation.type || '').includes('CONVERSATION')) scheduleSync();
  });
  // Deep-link: /kanban/conversations/:id aberto direto (ou F5) → abre o painel
  if (route.params.conversation_id) {
    openPanel({ id: parseInt(route.params.conversation_id, 10) });
  }
});

onBeforeUnmount(() => {
  if (unsubscribe) unsubscribe();
  clearTimeout(syncTimer);
});

// ── Drag & drop ──────────────────────────────────────────────────────────────
async function onColumnChange(evt, stage) {
  const added = evt.added && evt.added.element;
  if (!added) return;
  const ca = { ...(added.custom_attributes || {}) };
  if (stage === ENTRADA) delete ca[KANBAN_KEY];
  else ca[KANBAN_KEY] = stage;
  added.custom_attributes = ca;
  try {
    await store.dispatch('updateCustomAttributes', {
      conversationId: added.id,
      customAttributes: ca,
    });
  } catch (e) {
    useAlert('Erro ao mover conversa — recarregando quadro');
    fetchAll();
  }
}

// ── Ações do card ────────────────────────────────────────────────────────────
async function markUnread(c) {
  try {
    await ConversationApi.markMessagesUnread({ id: c.id });
    c.unread_count = Math.max(1, c.unread_count || 0);
    useAlert('Marcada como não lida');
  } catch (e) {
    useAlert('Erro ao marcar como não lida');
  }
}

async function resolveConv(c) {
  try {
    await store.dispatch('toggleStatus', {
      conversationId: c.id,
      status: 'resolved',
    });
    c.status = 'resolved'; // some do quadro (só mostramos abertas)
    rebuild();
    useAlert(`Conversa #${c.id} resolvida`);
  } catch (e) {
    useAlert('Erro ao resolver conversa');
  }
}

// ── Gestão de colunas ────────────────────────────────────────────────────────
async function saveStages(list) {
  savingCols.value = true;
  try {
    if (definition.value) {
      await store.dispatch('attributes/update', {
        id: definition.value.id,
        attribute_values: list,
      });
    } else {
      await store.dispatch('attributes/create', {
        attribute_display_name: 'Etapa do Atendimento (Kanban)',
        attribute_description:
          'Coluna do quadro Kanban de atendimento (gerenciado pelo botão Kanban)',
        attribute_model: 0, // conversation_attribute
        attribute_display_type: 6, // list
        attribute_key: KANBAN_KEY,
        attribute_values: list,
      });
    }
    await store.dispatch('attributes/get');
    rebuild();
  } catch (e) {
    useAlert('Erro ao salvar colunas');
  } finally {
    savingCols.value = false;
    menuOpenFor.value = null;
  }
}

function setupBoard() {
  saveStages(DEFAULT_STAGES);
}

function addColumn() {
  // eslint-disable-next-line no-alert
  const name = window.prompt('Nome da nova coluna:');
  if (!name || !name.trim()) return;
  const clean = name.trim();
  if (stages.value.includes(clean) || clean === ENTRADA) {
    useAlert('Já existe uma coluna com esse nome');
    return;
  }
  saveStages([...stages.value, clean]);
}

async function renameColumn(stage) {
  // eslint-disable-next-line no-alert
  const name = window.prompt('Novo nome da coluna:', stage);
  if (!name || !name.trim() || name.trim() === stage) return;
  const clean = name.trim();
  if (stages.value.includes(clean)) {
    useAlert('Já existe uma coluna com esse nome');
    return;
  }
  const cards = [...(columns.value[stage] || [])];
  await saveStages(stages.value.map(s => (s === stage ? clean : s)));
  // migra as conversas da coluna pro novo nome (senão cairiam na Entrada)
  await Promise.all(
    cards.map(c => {
      const ca = { ...(c.custom_attributes || {}), [KANBAN_KEY]: clean };
      c.custom_attributes = ca;
      return store
        .dispatch('updateCustomAttributes', {
          conversationId: c.id,
          customAttributes: ca,
        })
        .catch(() => {});
    })
  );
  rebuild();
}

function deleteColumn(stage) {
  const count = (columns.value[stage] || []).length;
  // eslint-disable-next-line no-alert
  const ok = window.confirm(
    `Excluir a coluna "${stage}"?` +
      (count ? ` As ${count} conversa(s) dela voltam para a Entrada.` : '')
  );
  if (!ok) return;
  saveStages(stages.value.filter(s => s !== stage));
}

function moveColumn(stage, dir) {
  const list = [...stages.value];
  const i = list.indexOf(stage);
  const j = i + dir;
  if (i < 0 || j < 0 || j >= list.length) return;
  [list[i], list[j]] = [list[j], list[i]];
  saveStages(list);
}

// ── Card helpers ─────────────────────────────────────────────────────────────
function contactName(c) {
  return (c.meta && c.meta.sender && c.meta.sender.name) || 'Sem nome';
}

function assigneeInitials(c) {
  const n =
    c.meta &&
    c.meta.assignee &&
    (c.meta.assignee.available_name || c.meta.assignee.name);
  if (!n) return null;
  return n
    .split(' ')
    .filter(Boolean)
    .slice(0, 2)
    .map(p => p[0])
    .join('')
    .toUpperCase();
}

function lastMessage(c) {
  const msgs = c.messages || [];
  const last = msgs[msgs.length - 1];
  const txt =
    (last && last.content) ||
    (c.last_non_activity_message && c.last_non_activity_message.content) ||
    '';
  return txt.replace(/\s+/g, ' ').slice(0, 70);
}

function cardBorder(c) {
  if (!needsReply(c)) return 'transparent';
  return waitingMinutes(c) >= 30 ? '#dc2626' : '#d97706';
}

function ageInfo(c) {
  const ts = (c.timestamp || 0) * 1000;
  if (!ts) return { label: '—', color: '#94a3b8' };
  const m = Math.max(0, Math.floor((Date.now() - ts) / 60000));
  const color = m < 60 ? '#16a34a' : m < 240 ? '#d97706' : '#dc2626';
  return { label: fmtMin(m), color };
}

function openConversation(c) {
  router.push({
    name: 'inbox_conversation',
    params: { accountId: route.params.accountId, conversation_id: c.id },
  });
}

function backToList() {
  router.push({ name: 'home', params: { accountId: route.params.accountId } });
}

// ── Painel lateral: conversa completa POR CIMA do quadro ────────────────────
const panelOpen = ref(false);

// Largura do drawer de conversa: ajustavel por arraste na borda esquerda,
// persistida por usuario (localStorage kanban_conv_w_<userId>).
const drawerWidth = ref(null);
try {
  const salvoW = localStorage.getItem("kanban_conv_w_" + (store.getters.getCurrentUserID || ""));
  if (salvoW) drawerWidth.value = Math.max(520, parseInt(salvoW, 10) || 0) || null;
} catch (e) { /* localStorage indisponivel */ }
const drawerStyle = computed(() =>
  drawerWidth.value
    ? { width: Math.min(drawerWidth.value, Math.round(window.innerWidth * 0.94)) + "px" }
    : {}
);
function startDrawerResize(e) {
  e.preventDefault();
  const startX = e.clientX;
  const drawerEl = document.getElementById("kanban-conv-drawer");
  const startW = drawerEl ? drawerEl.offsetWidth : 1150;
  const onMove = ev => {
    drawerWidth.value = Math.min(
      Math.max(startW + (startX - ev.clientX), 520),
      Math.round(window.innerWidth * 0.94)
    );
  };
  const onUp = () => {
    document.removeEventListener("mousemove", onMove);
    document.removeEventListener("mouseup", onUp);
    document.body.style.userSelect = "";
    document.body.style.cursor = "";
    try {
      localStorage.setItem("kanban_conv_w_" + (store.getters.getCurrentUserID || ""), String(drawerWidth.value || ""));
    } catch (e2) { /* sem persistencia */ }
  };
  document.body.style.userSelect = "none";
  document.body.style.cursor = "col-resize";
  document.addEventListener("mousemove", onMove);
  document.addEventListener("mouseup", onUp);
}
const panelInboxId = ref(0);
const openingPanel = ref(false);

async function openPanel(c) {
  if (openingPanel.value || dragging.value) return;
  openingPanel.value = true;
  try {
    const find = () =>
      (store.getters.getAllConversations || []).find(x => x.id === c.id);
    let chat = find();
    if (!chat) {
      await store.dispatch('getConversation', c.id);
      chat = find();
    }
    if (!chat) {
      useAlert('Não consegui abrir a conversa — tente pela lista');
      return;
    }
    await store.dispatch('setActiveChat', { data: chat });
    panelInboxId.value = chat.inbox_id;
    panelOpen.value = true;
    // URL espelha a conversa aberta → scripts injetados (cards de vendas/artes)
    // reconhecem a conversa; e o Back do navegador fecha o painel.
    if (String(route.params.conversation_id || '') !== String(c.id)) {
      router.push({
        name: 'kanban_board_conversation',
        params: {
          accountId: route.params.accountId,
          conversation_id: c.id,
        },
      });
    }
  } catch (e) {
    useAlert('Erro ao abrir a conversa');
  } finally {
    openingPanel.value = false;
  }
}

function closePanel() {
  panelOpen.value = false;
  store.dispatch('clearSelectedState');
  if (route.params.conversation_id) {
    router.push({
      name: 'kanban_board',
      params: { accountId: route.params.accountId },
    });
  }
}

// Sincroniza painel ⇆ URL (deep-link, F5 e botão voltar do navegador)
watch(
  () => route.params.conversation_id,
  id => {
    if (!id && panelOpen.value) {
      panelOpen.value = false;
      store.dispatch('clearSelectedState');
    } else if (id && !panelOpen.value) {
      openPanel({ id: parseInt(id, 10) });
    }
  }
);

const orderedStages = computed(() => [ENTRADA, ...stages.value]);

function stageTitle(s) {
  return s === ENTRADA ? '📥 Entrada' : s;
}

// ── Largura das colunas: arrastável e SALVA NA CONTA do atendente ────────────
// Persistência via ui_settings nativo do Chatwoot (viaja com o login pra
// qualquer PC). As COLUNAS são espelhadas pra todos; só a largura é pessoal.
const { uiSettings, updateUISettings } = useUISettings();
const COL_MIN = 220;
const COL_MAX = 640;
const COL_DEFAULT = 290;
const colWidths = ref({});
const resizingCol = ref(false);

watch(
  uiSettings,
  s => {
    if (!resizingCol.value && s && typeof s.kanban_col_widths === 'object') {
      colWidths.value = { ...(s.kanban_col_widths || {}) };
    }
  },
  { immediate: true, deep: true }
);

function colWidth(s) {
  const w = Number(colWidths.value[s]);
  return Number.isFinite(w) && w > 0
    ? Math.min(COL_MAX, Math.max(COL_MIN, w))
    : COL_DEFAULT;
}

function startColResize(e, s) {
  resizingCol.value = true;
  const startX = e.clientX;
  const startW = colWidth(s);
  const onMove = ev => {
    colWidths.value = {
      ...colWidths.value,
      [s]: Math.min(COL_MAX, Math.max(COL_MIN, startW + (ev.clientX - startX))),
    };
  };
  const onUp = () => {
    document.removeEventListener('mousemove', onMove);
    document.removeEventListener('mouseup', onUp);
    resizingCol.value = false;
    updateUISettings({ kanban_col_widths: { ...colWidths.value } });
  };
  document.addEventListener('mousemove', onMove);
  document.addEventListener('mouseup', onUp);
  e.preventDefault();
  e.stopPropagation();
}

function resetColWidth(s) {
  const next = { ...colWidths.value };
  delete next[s];
  colWidths.value = next;
  updateUISettings({ kanban_col_widths: next });
}
</script>

<template>
  <div class="flex flex-col w-full h-full min-h-0 bg-n-slate-2">
    <!-- Linha 1: título + ações -->
    <div
      class="flex items-center justify-between gap-2 px-4 h-[3rem] border-b border-n-weak shrink-0"
    >
      <div class="flex items-center gap-2 min-w-0">
        <h1 class="text-base font-medium text-n-slate-12 truncate">
          🗂️ Kanban de Atendimento
        </h1>
        <span
          class="px-2 py-1 rounded-md bg-n-slate-3 text-xxs text-n-slate-12 shrink-0"
        >
          {{ totalCount }} abertas
        </span>
        <span
          v-if="pendingTotal"
          class="px-2 py-1 rounded-md text-xxs font-bold text-white shrink-0"
          style="background: #dc2626"
        >
          🔥 {{ pendingTotal }} a responder
        </span>
      </div>
      <div class="flex items-center gap-1 shrink-0">
        <NextButton
          v-tooltip.top-end="'Recarregar quadro'"
          icon="i-lucide-refresh-cw"
          slate
          xs
          faded
          :is-loading="loading"
          @click="fetchAll"
        />
        <NextButton
          v-if="boardReady"
          v-tooltip.top-end="'Nova coluna'"
          icon="i-lucide-plus"
          slate
          xs
          faded
          :disabled="savingCols"
          @click="addColumn"
        />
        <NextButton
          v-tooltip.top-end="'Voltar para a lista'"
          icon="i-lucide-list"
          slate
          xs
          faded
          @click="backToList"
        />
      </div>
    </div>

    <!-- Linha 2: modo de visão + filtros por atendente/etiqueta -->
    <div
      v-if="boardReady"
      class="flex flex-wrap items-center gap-1.5 px-4 py-1.5 border-b border-n-weak shrink-0"
    >
      <input
        v-model="searchQ"
        type="search"
        placeholder="🔎 Buscar em tudo…"
        class="px-2.5 py-1 text-xs rounded-lg border border-n-weak bg-n-slate-1 text-n-slate-12 outline-none shrink-0"
        style="width: 200px; min-width: 0"
      />
      <!-- Toggle "Fluxo completo / 🔥 A responder" removido a pedido (2026-07-03):
           os pendentes já sobem ao topo com badge 🔴 e os filtros de tag cobrem
           o recorte. Lógica viewMode/setMode segue adormecida pra reativação. -->
      <span class="text-n-slate-10 text-xxs shrink-0">│</span>
<!-- Chip "👤 Meus" removido a pedido (2026-07-03) — lógica filterMine segue
           adormecida; pra reativar, restaurar este botão chamando toggleMine. -->
      <button
        v-for="l in boardLabels"
        :key="l.title"
        class="flex items-center gap-1.5 px-2 py-1 rounded-full text-xs font-semibold border shrink-0"
        :class="
          filterLabel === l.title
            ? 'text-white border-transparent'
            : 'text-n-slate-11 border-n-weak bg-n-slate-1'
        "
        :style="filterLabel === l.title ? `background:${l.color}` : ''"
        @click="setLabelFilter(l.title)"
      >
        <span
          class="w-2 h-2 rounded-full shrink-0"
          :style="{ background: l.color }"
        />
        {{ l.title }}
        <span class="opacity-70">{{ l.count }}</span>
      </button>
    </div>

    <!-- Setup inicial (sem colunas ainda) -->
    <div
      v-if="!boardReady && !loading"
      class="flex flex-col items-center justify-center flex-1 gap-4 p-8 text-center"
    >
      <div class="text-4xl">🗂️</div>
      <div class="max-w-md text-sm text-n-slate-11">
        O quadro ainda não tem colunas. Crie as etapas padrão de atendimento —
        depois você pode renomear, excluir e adicionar outras.
      </div>
      <NextButton
        label="Criar quadro com etapas padrão"
        :is-loading="savingCols"
        @click="setupBoard"
      />
    </div>

    <!-- Resultados da busca ampla (inclui resolvidas) -->
    <div
      v-else-if="showSearchResults"
      class="flex flex-col flex-1 min-h-0 gap-1.5 p-3 overflow-y-auto"
    >
      <div class="text-xs text-n-slate-11">
        {{
          searching
            ? 'Buscando em todas as conversas…'
            : searchResults.length + ' resultado(s) — clique pra abrir no painel'
        }}
      </div>
      <div
        v-for="r in searchResults"
        :key="r.id"
        class="flex items-center justify-between gap-2 p-2.5 rounded-lg cursor-pointer bg-n-slate-1 border border-n-weak hover:border-n-strong"
        @click="openSearchResult(r)"
      >
        <div class="flex flex-col min-w-0">
          <span class="text-sm font-medium truncate text-n-slate-12">
            {{ r.title }}
            <span class="text-xxs text-n-slate-10">{{ r.phone }}</span>
          </span>
          <span v-if="r.snippet" class="text-xs truncate text-n-slate-11">
            {{ r.snippet }}
          </span>
        </div>
        <span
          class="px-1.5 py-0.5 rounded text-xxs font-semibold shrink-0"
          :style="
            r.status === 'resolved'
              ? 'color:#16a34a;background:#16a34a18'
              : r.isContact
                ? 'color:#6d28d9;background:#6d28d918'
                : 'color:#2781f6;background:#2781f618'
          "
        >
          {{
            r.status === 'resolved'
              ? '✓ resolvida'
              : r.isContact
                ? '👤 contato'
                : r.status || 'aberta'
          }}
        </span>
      </div>
      <div
        v-if="!searching && !searchResults.length"
        class="p-6 text-sm text-center text-n-slate-11"
      >
        Nada encontrado. Tente outro termo.
      </div>
    </div>

    <!-- Board -->
    <div
      v-else
      class="flex flex-1 min-h-0 gap-3 p-3 overflow-x-auto overflow-y-hidden"
    >
      <div
        v-for="s in orderedStages"
        :key="s"
        class="relative flex flex-col shrink-0 min-h-0 rounded-xl bg-n-slate-3"
        :style="{ width: colWidth(s) + 'px' }"
      >
        <!-- Alça de redimensionar (arrasta; duplo-clique restaura o padrão) -->
        <div
          class="absolute top-0 bottom-0 ltr:-right-2 rtl:-left-2 z-10 flex justify-center w-4 cursor-col-resize hover:bg-n-alpha-1 rounded"
          :title="'Arraste pra ajustar a largura · duplo-clique restaura'"
          @mousedown="startColResize($event, s)"
          @dblclick="resetColWidth(s)"
        >
          <div class="w-0.5 h-full rounded bg-n-slate-6 opacity-0 hover:opacity-100" />
        </div>
        <!-- Header da coluna -->
        <div class="flex items-center justify-between gap-1 px-3 py-2 shrink-0">
          <div class="flex items-center gap-2 min-w-0">
            <span class="text-sm font-semibold truncate text-n-slate-12">
              {{ stageTitle(s) }}
            </span>
            <span
              class="px-1.5 py-0.5 rounded-md bg-n-slate-4 text-xxs font-semibold text-n-slate-11 shrink-0"
            >
              {{ (columns[s] || []).length }}
            </span>
            <span
              v-if="pendCount(s)"
              class="px-1.5 py-0.5 rounded-md text-xxs font-bold text-white shrink-0"
              style="background: #dc2626"
              :title="'Aguardando resposta'"
            >
              {{ pendCount(s) }}
            </span>
          </div>
          <div v-if="s !== ENTRADA" class="relative shrink-0">
            <NextButton
              icon="i-lucide-ellipsis"
              slate
              xs
              ghost
              @click="menuOpenFor = menuOpenFor === s ? null : s"
            />
            <div
              v-if="menuOpenFor === s"
              class="absolute z-50 flex flex-col mt-1 overflow-hidden text-sm rounded-lg shadow-lg ltr:right-0 rtl:left-0 bg-n-slate-1 border border-n-weak min-w-[170px]"
            >
              <button
                class="px-3 py-2 text-left text-n-slate-12 hover:bg-n-slate-3"
                @click="renameColumn(s)"
              >
                ✏️ Renomear
              </button>
              <button
                class="px-3 py-2 text-left text-n-slate-12 hover:bg-n-slate-3"
                @click="moveColumn(s, -1)"
              >
                ⬅️ Mover p/ esquerda
              </button>
              <button
                class="px-3 py-2 text-left text-n-slate-12 hover:bg-n-slate-3"
                @click="moveColumn(s, 1)"
              >
                ➡️ Mover p/ direita
              </button>
              <button
                class="px-3 py-2 text-left hover:bg-n-slate-3"
                style="color: #dc2626"
                @click="deleteColumn(s)"
              >
                🗑️ Excluir coluna
              </button>
            </div>
          </div>
        </div>

        <!-- Cards -->
        <draggable
          :list="columns[s] || []"
          group="kanban"
          item-key="id"
          class="flex flex-col flex-1 min-h-[60px] gap-2 px-2 pb-2 overflow-y-auto"
          ghost-class="kanban-ghost"
          @start="dragging = true"
          @end="dragging = false"
          @change="onColumnChange($event, s)"
        >
          <template #item="{ element }">
            <div
              class="flex flex-col gap-1.5 p-2.5 rounded-lg cursor-pointer bg-n-slate-1 border border-n-weak hover:border-n-strong"
              :style="{ borderLeft: `3px solid ${cardBorder(element)}` }"
              @click="openPanel(element)"
            >
              <!-- nome + não lidas -->
              <div class="flex items-center justify-between gap-2">
                <span class="text-sm font-medium truncate text-n-slate-12">
                  {{ contactName(element) }}
                </span>
                <span
                  v-if="element.unread_count"
                  class="px-1.5 py-0.5 rounded-full text-xxs font-bold text-white shrink-0"
                  style="background: #2781f6"
                >
                  {{ element.unread_count }}
                </span>
              </div>
              <!-- prévia -->
              <div class="text-xs truncate text-n-slate-11">
                {{ lastMessage(element) || '(sem mensagem)' }}
              </div>
              <!-- estado de resposta -->
              <div class="flex items-center gap-1">
                <span
                  v-if="needsReply(element)"
                  class="px-1.5 py-0.5 rounded text-xxs font-bold text-white"
                  :style="{ background: cardBorder(element) }"
                >
                  🔴 Responder · {{ fmtMin(waitingMinutes(element)) }}
                </span>
                <span
                  v-else
                  class="px-1.5 py-0.5 rounded text-xxs font-semibold"
                  style="color: #16a34a; background: #16a34a18"
                >
                  ✓ Respondida · com o cliente
                </span>
              </div>
              <!-- etiquetas (dono do lead) + indicador de pool -->
              <div
                v-if="(element.labels || []).length || semVendedor(element)"
                class="flex flex-wrap items-center gap-1"
              >
                <span
                  v-for="l in (element.labels || []).slice(0, 3)"
                  :key="l"
                  class="flex items-center gap-1 px-1.5 py-0.5 rounded-full text-xxs font-semibold border border-n-weak text-n-slate-11"
                >
                  <span
                    class="w-1.5 h-1.5 rounded-full"
                    :style="{ background: labelColorMap[l] || '#64748b' }"
                  />
                  {{ l }}
                </span>
                <span
                  v-if="semVendedor(element)"
                  class="px-1.5 py-0.5 rounded-full text-xxs font-bold border border-dashed"
                  style="color: #d97706; border-color: #d97706"
                  :title="'Sem dono — vira de quem responder primeiro'"
                >
                  🆓 disponível
                </span>
              </div>
              <!-- rodapé: meta + ações -->
              <div class="flex items-center justify-between gap-1 mt-0.5">
                <div class="flex items-center gap-1 min-w-0">
                  <span
                    class="px-1.5 py-0.5 rounded text-xxs font-semibold shrink-0"
                    :style="{
                      color: ageInfo(element).color,
                      background: `${ageInfo(element).color}18`,
                    }"
                    :title="'Última atividade'"
                  >
                    ⏱ {{ ageInfo(element).label }}
                  </span>
                  <span class="text-xxs truncate text-n-slate-10">
                    #{{ element.id }}
                  </span>
                  <span
                    v-if="assigneeInitials(element)"
                    class="flex items-center justify-center w-4.5 h-4.5 p-0.5 rounded-full text-xxs font-bold text-white shrink-0"
                    style="background: #6d28d9"
                    :title="element.meta?.assignee?.name"
                  >
                    {{ assigneeInitials(element) }}
                  </span>
                </div>
                <div class="flex items-center gap-0.5 shrink-0">
                  <button
                    v-tooltip.top-end="'Marcar como não lida'"
                    class="px-1.5 py-0.5 rounded text-xxs text-n-slate-11 bg-n-slate-3 hover:bg-n-slate-4"
                    @click.stop="markUnread(element)"
                  >
                    ✉️
                  </button>
                  <button
                    v-tooltip.top-end="'Resolver conversa'"
                    class="px-1.5 py-0.5 rounded text-xxs text-n-slate-11 bg-n-slate-3 hover:bg-n-slate-4"
                    @click.stop="resolveConv(element)"
                  >
                    ✅
                  </button>
                  <button
                    v-tooltip.top-end="'Abrir em tela cheia'"
                    class="px-1.5 py-0.5 rounded text-xxs text-n-slate-11 bg-n-slate-3 hover:bg-n-slate-4"
                    @click.stop="openConversation(element)"
                  >
                    ⛶
                  </button>
                </div>
              </div>
            </div>
          </template>
        </draggable>
      </div>

      <!-- Nova coluna (atalho no fim do board) -->
      <div v-if="boardReady" class="flex items-start w-[220px] shrink-0 pt-1">
        <button
          class="w-full px-3 py-2 text-sm border border-dashed rounded-xl text-n-slate-11 border-n-strong hover:bg-n-slate-3"
          :disabled="savingCols"
          @click="addColumn"
        >
          ＋ Nova coluna
        </button>
      </div>
    </div>

    <!-- Drawer: conversa completa por cima do quadro -->
    <div v-if="panelOpen" class="fixed inset-0 z-40 flex justify-end">
      <div
        class="absolute inset-0"
        style="background: rgba(0, 0, 0, 0.45)"
        @click="closePanel"
      />
      <div
        id="kanban-conv-drawer"
        class="relative flex flex-col h-full w-[min(1150px,94vw)] shadow-2xl bg-n-slate-1 border-l border-n-weak"
        :style="drawerStyle"
      >
        <div
          title="Arraste para ajustar a largura"
          class="absolute left-0 top-0 bottom-0 z-30 hover:bg-n-brand/20"
          style="width: 7px; cursor: col-resize"
          @mousedown="startDrawerResize"
        />
        <div
          class="flex items-center justify-between px-3 py-2 border-b border-n-weak shrink-0"
        >
          <span class="text-sm font-medium text-n-slate-11">
            Atendendo pelo Kanban — feche para voltar ao quadro
          </span>
          <NextButton
            v-tooltip.top-end="'Fechar (voltar ao quadro)'"
            icon="i-lucide-x"
            slate
            xs
            faded
            @click="closePanel"
          />
        </div>
        <ConversationBox
          :inbox-id="panelInboxId"
          :is-on-expanded-layout="true"
          class="flex-1 min-h-0"
        />
      </div>
    </div>
  </div>
</template>

<style scoped>
.kanban-ghost {
  opacity: 0.45;
  border: 2px dashed #2781f6 !important;
}
</style>
